import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PerModal } from '../Modal'
import PerText from '../Text'
import { KudosLegalEntityVisibilityDto, KudosLegalEntityVisibilityFilterInput, KudosLegalEntityVisibilityInput } from '../../../types'
import { editKudosLegalEntityVisibility } from '../../../graphql/kudos/useKudosMutations'
import { showNotification } from '../Notification/notification'

export interface EditKudosLegalEntityVisibilityModalProps {
  visible: boolean
  onClose: () => void
  kudosLegalEntityVisibility: KudosLegalEntityVisibilityDto | null
  filters: KudosLegalEntityVisibilityFilterInput
  resetFiltersAfterVisibilityChange: () => void
}

export const EditKudosLegalEntityVisibilityModal: FC<EditKudosLegalEntityVisibilityModalProps> = ({
  visible,
  onClose,
  kudosLegalEntityVisibility,
  filters,
  resetFiltersAfterVisibilityChange,
}) => {
  const { t } = useTranslation()

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')

  const handleEdit = async () => {
    if (!kudosLegalEntityVisibility) return

    setIsLoading(true)
    setErrorMessage('')

    const input: KudosLegalEntityVisibilityInput = {
      legalEntityId: kudosLegalEntityVisibility.legalEntityId,
      kudosEnabled: !kudosLegalEntityVisibility.kudosEnabled,
    }
    const result = await editKudosLegalEntityVisibility(input, filters)

    if (!!result.data && !result.errors) {
      resetAndClose()
      resetFiltersAfterVisibilityChange()
      showNotification({
        type: 'success',
        message: t('kudos.notifications.editLegalEntityVisibility.successTitle'),
        description: t('kudos.notifications.editLegalEntityVisibility.successDescription'),
      })
    } else {
      const errorMessage =
        result.errors?.[0]?.extensions?.validationErrors?.[0]?.message || t('kudos.settings.editLegalEntityVisibilityModal.error')
      setErrorMessage(errorMessage)
      setIsLoading(false)
    }
  }

  const resetAndClose = () => {
    setErrorMessage('')
    setIsLoading(false)
    onClose()
  }

  if (!kudosLegalEntityVisibility) return null

  return (
    <PerModal
      title={{ heading: t('kudos.settings.editLegalEntityVisibilityModal.title') }}
      size="medium"
      centered
      visible={visible}
      onCancel={resetAndClose}
      footer={{
        cancelBtn: {
          onClick: resetAndClose,
        },
        confirmBtn: {
          onClick: handleEdit,
          disabled: isLoading || !!errorMessage,
          isProcessing: { active: isLoading },
        },
      }}
    >
      {kudosLegalEntityVisibility.kudosEnabled ? (
        <>
          <PerText fontSize="m">{t(`kudos.settings.editLegalEntityVisibilityModal.disable1`)}</PerText>
          <PerText fontSize="m">{t(`kudos.settings.editLegalEntityVisibilityModal.disable2`)}</PerText>
          <PerText fontSize="m" mt="xxs" fontWeight="bold">
            {t(`kudos.settings.editLegalEntityVisibilityModal.disable3`)}
          </PerText>
        </>
      ) : (
        <>
          <PerText fontSize="m">{t(`kudos.settings.editLegalEntityVisibilityModal.enable1`)}</PerText>
          <PerText fontSize="m">{t(`kudos.settings.editLegalEntityVisibilityModal.enable2`)}</PerText>
          <PerText fontSize="m" mt="xxs" fontWeight="bold">
            {t(`kudos.settings.editLegalEntityVisibilityModal.enable3`)}
          </PerText>
        </>
      )}

      {errorMessage && (
        <PerText color="error" fontSize="m" mt="xs">
          {errorMessage}
        </PerText>
      )}
    </PerModal>
  )
}

export default EditKudosLegalEntityVisibilityModal
